import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  MessageSquare,
  Send,
  AlertCircle,
  GraduationCap,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WrittenInterviewScoring() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clarificationText, setClarificationText] = useState({});
  const [sendingClarification, setSendingClarification] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/written-interview/score/${token}`);
      if (!response.ok) throw new Error("Invalid or expired link");
      const result = await response.json();
      setData(result);
      // Pre-fill existing scores
      const existing = {};
      result.qa_pairs.forEach(qa => {
        if (qa.existing_score) {
          existing[qa.question_id] = {
            result: qa.existing_score.result,
            remarks: qa.existing_score.remarks || "",
          };
        }
      });
      setScores(existing);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScores = async () => {
    const allScored = data.qa_pairs.every(qa => scores[qa.question_id]?.result);
    if (!allScored) {
      toast.error("Please score all questions as Pass or Fail before submitting");
      return;
    }

    setSubmitting(true);
    try {
      const formatted = data.qa_pairs.map(qa => ({
        question_id: qa.question_id,
        result: scores[qa.question_id]?.result,
        remarks: scores[qa.question_id]?.remarks || "",
      }));

      const response = await fetch(`${API_URL}/api/written-interview/score/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scores: formatted, assessor_feedback: feedback }),
      });

      if (!response.ok) throw new Error("Failed to submit scores");
      const result = await response.json();
      toast.success(`Scores submitted! Overall result: ${result.overall_result}`);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendClarification = async (questionId) => {
    const text = clarificationText[questionId]?.trim();
    if (!text) {
      toast.error("Please type your clarification question");
      return;
    }

    setSendingClarification(questionId);
    try {
      const response = await fetch(`${API_URL}/api/written-interview/score/${token}/clarification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_id: questionId, clarification_text: text }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to send clarification");
      }
      toast.success("Clarification sent to candidate");
      setClarificationText({ ...clarificationText, [questionId]: "" });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingClarification(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md" data-testid="written-interview-scoring-link-expired">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid or Expired</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 text-left">
              <p className="font-semibold mb-1">Need a fresh scoring link?</p>
              <p>Reply to your invitation email or contact the RPL administrator to ask for a <span className="font-semibold">Resend Assessor Link</span>. They can issue a new email in one click.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Scores Submitted</h2>
            <p className="text-slate-500">Thank you for reviewing the candidate's answers. The IQA team has been notified.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-slate-900">Credentia AI</h1>
            <p className="text-xs text-slate-500">Written Interview Assessment</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Candidate Info */}
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{data.candidate_name}</h2>
                <p className="text-slate-600">{data.candidate_role} at {data.candidate_employer}</p>
                {data.qualification && <p className="text-sm text-purple-600 mt-1">Qualification: {data.qualification}</p>}
              </div>
              <Badge className="bg-purple-600">{data.status?.replace(/_/g, " ")}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-800 mb-1">Instructions</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Review each answer and mark as <strong>Pass</strong> or <strong>Fail</strong></li>
                  <li>Add remarks to explain your scoring decision</li>
                  <li>You can send up to <strong>3 follow-up clarification questions</strong> per answer if needed</li>
                  <li>Once all questions are scored, click "Submit Final Scores"</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Q&A Pairs */}
        {data.qa_pairs.map((qa, idx) => {
          const score = scores[qa.question_id] || {};
          const clarifications = qa.existing_score?.clarifications || [];
          const canSendMore = clarifications.length < data.max_clarifications_per_question;

          return (
            <Card key={qa.question_id} className="border-slate-200" data-testid={`scoring-card-${idx}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {qa.question_number}
                  </div>
                  <div>
                    <CardTitle className="text-sm font-medium text-slate-900">{qa.question_text}</CardTitle>
                    <CardDescription className="text-xs mt-1">Related to: {qa.competency_name}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Candidate's Answer */}
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1 font-medium">Candidate's Answer:</p>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{qa.answer_text || "No answer provided"}</p>
                </div>

                {/* Clarifications */}
                {clarifications.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-500">Follow-up Clarifications ({clarifications.length}/{data.max_clarifications_per_question}):</p>
                    {clarifications.map((c, ci) => (
                      <div key={ci} className={`p-3 rounded-lg border text-sm ${c.answer_text ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                        <p className="font-medium text-slate-700">
                          <span className="text-xs text-slate-400 mr-1">Clarification #{ci + 1}:</span>
                          {c.question_text}
                        </p>
                        {c.answer_text ? (
                          <div className="mt-2 p-2 bg-white rounded border border-green-200">
                            <p className="text-xs text-green-600 font-medium mb-0.5">Candidate's Reply:</p>
                            <p className="text-slate-800 whitespace-pre-wrap">{c.answer_text}</p>
                          </div>
                        ) : (
                          <p className="text-amber-600 italic mt-1 text-xs">Awaiting candidate's response...</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Scoring Controls */}
                <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant={score.result === "pass" ? "default" : "outline"}
                    className={score.result === "pass" ? "bg-green-600 hover:bg-green-700" : ""}
                    onClick={() => setScores({ ...scores, [qa.question_id]: { ...score, result: "pass" } })}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Pass
                  </Button>
                  <Button
                    size="sm"
                    variant={score.result === "fail" ? "default" : "outline"}
                    className={score.result === "fail" ? "bg-red-600 hover:bg-red-700" : ""}
                    onClick={() => setScores({ ...scores, [qa.question_id]: { ...score, result: "fail" } })}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Fail
                  </Button>
                  <Input
                    placeholder="Remarks (optional)"
                    className="flex-1 h-9 text-sm"
                    value={score.remarks || ""}
                    onChange={(e) => setScores({ ...scores, [qa.question_id]: { ...score, remarks: e.target.value } })}
                  />
                </div>

                {/* Send Clarification */}
                {canSendMore && (
                  <div className="flex items-center gap-2 pt-2">
                    <Input
                      placeholder="Ask a follow-up clarification question..."
                      className="flex-1 h-9 text-sm"
                      value={clarificationText[qa.question_id] || ""}
                      onChange={(e) => setClarificationText({ ...clarificationText, [qa.question_id]: e.target.value })}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      disabled={sendingClarification === qa.question_id}
                      onClick={() => handleSendClarification(qa.question_id)}
                    >
                      {sendingClarification === qa.question_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><MessageSquare className="w-4 h-4 mr-1" />Clarify</>
                      )}
                    </Button>
                  </div>
                )}
                {!canSendMore && clarifications.length > 0 && (
                  <p className="text-xs text-slate-400">Maximum clarifications reached for this question</p>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Overall Feedback & Submit */}
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Overall Feedback (optional)</Label>
              <Textarea
                placeholder="General comments about the candidate's written responses..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSubmitScores} disabled={submitting} size="lg" data-testid="submit-final-scores-btn">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Submit Final Scores</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
